import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Zap } from "lucide-react";
import { SERVICE_CATEGORIES, useServiceCategories, categoryHref } from "../lib/categories";
import SameDayBadge from "../components/SameDayBadge";
import SameDayCarousel from "../components/SameDayCarousel";
import FlagshipSpotlight from "../components/FlagshipSpotlight";
import { useSeoHead } from "../lib/seo";
import { useIsMobile } from "../hooks/useIsMobile";
import { MobileHero, MobileCategoryList } from "../components/mobile/MobileHome";

// User-provided exact hero spec — used as the actual hero background so the
// page matches the reference 1:1. A clickable hotspot overlays the visible
// "EXPLORE OUR CAPABILITIES" button inside the image.
const HERO_REFERENCE_IMG = "/hero/hero-2400.webp";

// Drafting corner-mark — used decoratively on cards and the hero frame.
const CornerMark = ({ className = "", rotate = 0 }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    className={className}
    style={{ transform: `rotate(${rotate}deg)` }}
    aria-hidden
  >
    <path d="M1 1 L8 1 M1 1 L1 8" stroke="currentColor" strokeWidth="1.25" />
  </svg>
);

function CategoryCard({ cat, index, totalCount }) {
  const Icon = cat.icon;
  // Prefer the bundled local schematic — it ships with every deploy so it
  // never breaks when an admin-uploaded file goes missing from storage.
  // The admin-uploaded `cat.image` still wins if the slug isn't one of the
  // bundled built-ins (e.g. a newly admin-created category).
  const BUNDLED_SLUGS = [
    "roll-labels", "digital-print", "offset-print",
    "retractables-banners", "booklets-books", "presentation-folders", "wedding-collateral",
    "custom-roll-labels",
  ];
  const bundled = BUNDLED_SLUGS.includes(cat.slug) ? `/categories/${cat.slug}.webp` : "";
  const imgSrc = bundled || cat.image || "";
  return (
    <Link
      to={categoryHref(cat.slug)}
      className="group relative bg-white border border-slate-200 hover:border-kolt-orange transition-colors duration-300 flex flex-col h-full overflow-hidden"
      data-testid={`category-card-${cat.slug}`}
    >
      {/* Corner marks */}
      <CornerMark className="absolute top-2 left-2 text-slate-300 group-hover:text-kolt-orange transition-colors z-10" />
      <CornerMark className="absolute top-2 right-2 text-slate-300 group-hover:text-kolt-orange transition-colors z-10" rotate={90} />
      <CornerMark className="absolute bottom-2 left-2 text-slate-300 group-hover:text-kolt-orange transition-colors z-10" rotate={-90} />
      <CornerMark className="absolute bottom-2 right-2 text-slate-300 group-hover:text-kolt-orange transition-colors z-10" rotate={180} />

      {/* Image */}
      <div className="relative aspect-[5/4] bg-slate-50 overflow-hidden">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={cat.name}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
            loading="lazy"
            onError={(e) => {
              // If the bundled file 404s for any reason, fall back to the
              // admin-uploaded image (if present). If that also fails, hide
              // the broken icon entirely.
              if (cat.image && e.currentTarget.src !== cat.image) {
                e.currentTarget.src = cat.image;
              } else {
                e.currentTarget.style.display = "none";
              }
            }}
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300">
            <Icon className="w-16 h-16" strokeWidth={0.8} />
          </div>
        )}
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur px-2.5 py-1.5">
          <Icon className="w-3.5 h-3.5 text-kolt-orange" />
          <span className="font-mono text-[10px] tracking-[0.22em] font-bold text-kolt-slate-dark uppercase">
            {String(index + 1).padStart(2, "0")} / {String(totalCount).padStart(2, "0")}
          </span>
        </div>
        {!cat.available && (
          <span className="absolute top-4 right-4 bg-kolt-slate-dark text-white text-[9px] font-mono tracking-[0.22em] font-bold uppercase px-2.5 py-1">
            Coming Soon
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-400 mb-3">
          {cat.spec}
        </div>
        <h3 className="font-display font-black text-xl text-kolt-slate-dark leading-tight mb-2 group-hover:text-kolt-orange transition-colors uppercase">
          {cat.name}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-5 flex-1">
          {cat.short}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <span className="font-mono text-[11px] tracking-[0.22em] uppercase font-bold text-kolt-slate-dark group-hover:text-kolt-orange transition-colors">
            {cat.available ? "View Catalog" : "Get Notified"}
          </span>
          <ArrowUpRight className="w-5 h-5 text-kolt-slate-dark group-hover:text-kolt-orange group-hover:rotate-12 transition-all" />
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const { categories: liveCategories } = useServiceCategories();
  // Fall back to the static seed list while the backend list is loading
  // so the hero block never paints empty.
  const categories = liveCategories.length ? liveCategories : SERVICE_CATEGORIES;
  const totalCats = categories.length;
  const isMobile = useIsMobile();
  useSeoHead({
    title: "Kolt Printing — Custom Printing You'll Actually Love | Same-Day Shipping",
    description:
      "Houston-based B2B and trade printer — business cards, roll labels, hang tags, banners, custom books, wedding stationery, foil stamp, embossing, letterpress and Pantone color matching. Live pricing, trade discounts, ships nationwide.",
    canonical: "/",
    image: "/branding/og-default.png",
    ogType: "website",
  });
  return (
    <main data-testid="home-page" className="bg-kolt-paper-soft">
      {isMobile ? <MobileHero /> : (
      <section
        className="relative bg-kolt-paper-soft border-b border-slate-200"
        data-testid="hero-section"
      >
        {/* Hidden semantic h1 so screen-readers + SEO can still index the brand promise. */}
        <h1 className="sr-only">
          KOLT PRINTING — Custom printing you&apos;ll actually love. Friendly online printing for small businesses, agencies and growing brands.
        </h1>

        <div className="relative w-full max-w-[1600px] mx-auto">
          <img
            src={HERO_REFERENCE_IMG}
            srcSet="/hero/hero-640.webp 640w, /hero/hero-1000.webp 1000w, /hero/hero-1600.webp 1600w, /hero/hero-2400.webp 2400w"
            sizes="(max-width: 768px) 100vw, min(100vw, 1600px)"
            width={1600}
            height={705}
            fetchpriority="high"
            decoding="async"
            alt="Kolt Printing — custom printing you'll actually love. Foil-stamped business cards, calipers, mechanical pencil and precision blueprint drawings on whitestock paper."
            className="w-full h-auto block select-none"
            draggable={false}
            data-testid="hero-image"
          />

          {/* Same Day Print & Ship CTA — positioned over the printed "EXPLORE OUR CAPABILITIES"
              area on the hero (left side). Shifted ~1cm up, ~1cm left, then +3 mm back down. */}
          <div
            className="absolute"
            style={{ left: "calc(8% - 38px)", top: "calc(63% - 27px)", maxWidth: "44%" }}
            data-testid="hero-same-day-wrap"
          >
            <SameDayBadge testid="hero-same-day-cta" />
          </div>

          {/* Optional secondary hotspot for the right-side blueprint area — mobile-only
              fallback. On md+ the visible orange pill below is the primary click target,
              so we hide this hotspot to prevent it from stealing clicks. */}
          <Link
            to="/quote"
            aria-label="Request a quote"
            className="md:hidden absolute focus:outline-none focus:ring-2 focus:ring-kolt-orange"
            style={{ left: "55%", top: "8%", width: "40%", height: "30%" }}
            data-testid="hero-quote-cta-hotspot"
          />

          {/* Visible "Request a Custom Quote" CTA — pinned to the top-right blueprint
              zone of the hero. Uses the brutalist mono/orange treatment so it
              reads as an engineering callout rather than a generic web button.
              Sub-line advertises the human 2-hour SLA to lift click-through. */}
          <Link
            to="/quote"
            className="hidden md:flex absolute group flex-col items-start gap-1 bg-kolt-orange hover:bg-kolt-orange-hover text-white font-mono tracking-[0.24em] font-black uppercase pl-4 pr-4 py-3 shadow-[6px_6px_0_rgba(51,63,72,0.95)] hover:shadow-[3px_3px_0_rgba(51,63,72,0.95)] hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150"
            style={{ right: "3.5%", top: "5.5%" }}
            data-testid="hero-quote-cta"
            aria-label="Request a custom quote"
          >
            <span className="flex items-center gap-2 text-[11px]">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" aria-hidden />
              <span>Request a Custom Quote</span>
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-12 transition-transform" />
            </span>
            <span className="text-[9px] tracking-[0.18em] font-normal normal-case text-white/80 pl-3.5">
              Human response within 2 hours
            </span>
          </Link>

          {/* Rush-job CTA — black plate with metallic copper foil type, stacked
              directly under the orange quote callout. */}
          <Link
            to="/quote?rush=1"
            className="hidden md:flex absolute group flex-col items-start gap-1 bg-kolt-paper-soft hover:bg-kolt-paper-light border border-[#B87333]/60 font-mono tracking-[0.2em] font-black uppercase pl-4 pr-4 py-3 shadow-[6px_6px_0_rgba(184,115,51,0.85)] hover:shadow-[3px_3px_0_rgba(184,115,51,0.85)] hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150"
            style={{ right: "3.5%", top: "16.5%" }}
            data-testid="hero-rush-cta"
            aria-label="Rush job? Share the specs and we'll do our best to save the day"
          >
            <span className="flex items-center gap-2 text-[11px] text-copper-foil">
              <Zap className="w-3.5 h-3.5 text-[#B87333]" aria-hidden />
              <span>Rush Job?</span>
              <ArrowUpRight className="w-4 h-4 text-[#B87333] group-hover:rotate-12 transition-transform" />
            </span>
            <span className="text-[9px] tracking-[0.14em] font-semibold normal-case text-[#8B4513] pl-5">
              Share the specs and we&rsquo;ll do our best to save the day!
            </span>
          </Link>

          {/* Mobile fallback — full-width pinned strip just below the hero,
              because the shadow-offset pill above is hidden < md to avoid
              overlapping the reference art on narrow viewports. */}
          <Link
            to="/quote"
            className="md:hidden absolute left-3 right-3 bottom-3 flex flex-col items-center gap-0.5 bg-kolt-orange text-white font-mono tracking-[0.24em] font-black uppercase py-2.5 shadow-[4px_4px_0_rgba(51,63,72,0.95)]"
            data-testid="hero-quote-cta-mobile"
          >
            <span className="flex items-center gap-2 text-[11px]">
              Request a Custom Quote
              <ArrowUpRight className="w-4 h-4" />
            </span>
            <span className="text-[9px] tracking-[0.18em] font-normal normal-case text-white/80">
              Human response within 2 hours
            </span>
          </Link>
        </div>
      </section>
      )}

      {/* ───────── SAME DAY LINEUP CAROUSEL ───────── */}
      <SameDayCarousel />

      {/* ───────── TRUST STRIP ───────── */}
      <section className="border-y border-slate-200 bg-kolt-inkwell text-white" data-testid="trust-strip">
        <div className="container-page py-7">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-8">
            {[
              ["★ 4.9", "From happy customers"],
              ["100%", "Reprint guarantee"],
              ["24h", "Same-day cutoff"],
              ["Free", "Design review on every order"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center gap-4">
                <span className="w-1.5 h-10 bg-kolt-orange" />
                <div>
                  <div className="font-display font-black text-xl text-white leading-none">{k}</div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/60 mt-1">{v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── FLAGSHIP: ROLL LABELS ───────── */}
      <FlagshipSpotlight basePath="/labels" />
      <FlagshipSpotlight basePath="/large-format" flip />

      {/* ───────── CATEGORIES (7) ───────── */}
      <section className={`bg-white ${isMobile ? "py-10" : "py-20 lg:py-24"}`} data-testid="category-section">
        <div className="container-page">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-12 bg-kolt-orange" />
                <span className="font-mono text-[11px] tracking-[0.3em] font-bold uppercase text-kolt-orange">
                  Our Capabilities / {String(totalCats).padStart(2, "0")} Categories
                </span>
              </div>
              <h2 className="font-display font-black text-4xl lg:text-5xl text-kolt-slate-dark tracking-tight leading-[1.05] uppercase max-w-2xl">
                One trusted print shop.
                <br />
                Seven products you&apos;ll love.
              </h2>
            </div>
            <p className="text-sm text-slate-500 max-w-md leading-relaxed lg:text-right">
              We are mostly a B2B printer — brands, agencies, retailers and print
              resellers make up most of our orders — so every product runs through
              the same color management and quality checks and looks consistently
              great, reorder after reorder.
            </p>
          </div>

          {isMobile ? (
            <MobileCategoryList categories={categories} />
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {categories.map((cat, i) => (
              <CategoryCard key={cat.slug} cat={cat} index={i} totalCount={totalCats} />
            ))}
          </div>
          )}
        </div>
      </section>

      {/* ───────── DEEP CTA ───────── */}
      <section className="bg-kolt-inkwell text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 blueprint-grid-dense opacity-[0.07] pointer-events-none" />
        <div className="container-page py-20 relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-12 bg-kolt-orange" />
              <span className="font-mono text-[11px] tracking-[0.3em] font-bold uppercase text-kolt-orange">
                Custom Quote · Free in 4 Hours
              </span>
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl uppercase tracking-tight leading-[1.05] mb-6">
              Tell us what you&apos;re printing —
              <br />
              we&apos;ll handle the rest.
            </h2>
            <p className="text-white/70 leading-relaxed mb-8 max-w-lg">
              Send your artwork, brand colors or a rough idea. Our friendly team
              gets back to you with a clear, no-surprises quote within 2 business hours.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/quote"
                className="inline-flex items-center gap-3 bg-kolt-orange hover:bg-kolt-orange-hover text-white font-mono text-xs tracking-[0.22em] font-bold uppercase px-7 py-4 transition-colors"
                data-testid="cta-quote-btn"
              >
                Request a Custom Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/shop"
                className="inline-flex items-center gap-3 border border-white/40 hover:bg-white hover:text-kolt-inkwell text-white font-mono text-xs tracking-[0.22em] font-bold uppercase px-7 py-4 transition-colors"
                data-testid="cta-browse-btn"
              >
                Browse Products
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              ["Small Business", "Cards, signage, brochures"],
              ["Restaurants & Cafés", "Menus, table tents, takeout"],
              ["Boutiques & Beauty", "Custom labels & hang tags"],
              ["Craft Beverage", "Wine, beer & spirits labels"],
              ["Agencies & Designers", "Brand-perfect client work"],
              ["Online Stores", "Packaging inserts & stickers"],
            ].map(([title, sub]) => (
              <div
                key={title}
                className="border border-white/15 hover:border-kolt-orange transition-colors p-4 group"
              >
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-kolt-orange mb-2">
                  We print for
                </div>
                <div className="font-display font-bold text-sm text-white leading-snug mb-1 group-hover:text-kolt-orange transition-colors">
                  {title}
                </div>
                <div className="text-xs text-white/60 leading-relaxed">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
